import { makeStore } from '../makeStore'
import { INBOX_CONVERSATION_TAG_DEFAULTS } from './inboxConversationTagDefaults'
import { loadInboxSettings } from './inboxSettingsStore'
import { defaultViewToActiveInboxTab } from './inboxSettingsStore'
import type { ISalesNavigatorData } from '../../services/inbox/salesNavigatorAccess'
import type { ISharedInboxAccount } from '../../services/apis/sharedInbox/useSharedInboxRequests'

const persistedInboxSettings = loadInboxSettings(INBOX_CONVERSATION_TAG_DEFAULTS)

const initialState = {
    // Modal
    modal: { type: null as string | null },

    // Tabs
    selectedTab: 'Inbox' as string,
    activeInboxLabel: defaultViewToActiveInboxTab(persistedInboxSettings.defaultView) as string,

    // Fetching
    fetchingConversations: true,
    fetchingMoreConversations: false,
    fetchingMessages: false,
    fetchingMoreMessages: false,

    // Conversations
    conversations: [] as any[],
    filteredConversations: [] as any[],
    selectedConversations: [] as any[],
    allConversationsFetched: false,
    allConversationLoaded: false,
    lastFetchedConversation: {} as any,
    pinnedConversationsArray: [] as any[],

    // Filters
    filteringTags: [] as any[],
    numberOfFilters: 0,
    pinned: 'All' as string,
    starred: 'All' as string,
    readed: 'All' as string,
    tagged: 'All' as string,
    archived: 'No' as string,
    tags: [] as any[],
    query: '' as string,
    lastMessagedFrom: '' as string,
    lastMessagedTo: '' as string,
    /** Parity with Contacts "Show last messaged only" (passed to API; same semantics as Connections `onlyLast`). */
    lastMessagedOnlyLast: false,
    conversationStatus: { value: 'all', label: 'All conversations' } as any,
    /** Same shape as Connections filter: `{ id, value, object }[]` — limits inbox rows by LeadDelta `user` on the conversation. */
    conversationOwners: undefined as undefined | any[],

    // Selected state
    selectedConversation: null as any,
    selectedConnection: null as any,
    selectedProfile: null as any,
    selectedParticipants: [] as any[],
    searchConnectionsQuery: { page: 1, pageSize: 10, searchByName: '' },

    // Messages
    messages: [] as any[],
    allMessagesFetched: true,
    messageAttachments: [] as any[],
    appliedTemplate: {} as any,

    // UI
    conversationDetails: true,

    /**
     * LinkedIn / Sales Navigator inbox pagination cursor (from extension sync response).
     *  - undefined: state not yet known (no extension sync done in this session).
     *  - null / '' (empty string): source confirmed there are no more pages.
     *  - non-empty string: pass as `nextCursor` on the next extension sync.
     * For classic Inbox this comes from `inboxGetConversationsV1`; for SN from `inboxGetSalesNavigatorConversations`.
     */
    linkedinNextCursor: undefined as string | null | undefined,
    /** Sales Navigator inbox/search pagination cursor (from extension SN sync). */
    salesNavigatorNextCursor: undefined as string | null | undefined,

    /** Live LI premium + SN inbox probe — plan + session for Sales Nav tab. */
    salesNavigatorData: null as ISalesNavigatorData | null,

    // Shared inbox only
    /** Proxy accounts the user may see, with unread counts and sync state. */
    sharedAccounts: [] as ISharedInboxAccount[],
    /** Accounts the list is narrowed to; empty = all of them. */
    sharedAccountFilter: [] as string[],
    /** Older-messages cursor for the open thread (Unipile pages by cursor, not by timestamp). */
    messagesCursor: null as string | null,
}

export type TInboxState = typeof initialState

const leanObjectIdString = (ref: unknown): string => {
    if (ref == null) return ''
    if (typeof ref === 'string') return ref
    if (typeof ref === 'object' && ref !== null && '$oid' in ref && typeof (ref as { $oid: unknown }).$oid === 'string') {
        return (ref as { $oid: string }).$oid
    }
    if (typeof ref === 'object' && ref !== null && '_id' in ref) {
        return leanObjectIdString((ref as { _id: unknown })._id)
    }
    return String(ref)
}

const timestampValue = (value: any) => {
    if (value == null || value === '') return 0
    const numeric = typeof value === 'number' ? value : Number(value)
    if (Number.isFinite(numeric)) return numeric
    const parsed = Date.parse(value)
    return Number.isFinite(parsed) ? parsed : 0
}

const sortConversations = (conversations: any[]) => [...conversations].sort((a: any, b: any) => {
    if (a?.isPinned && !b?.isPinned) return -1
    if (!a?.isPinned && b?.isPinned) return 1
    return timestampValue(b?.dateTimestamp) - timestampValue(a?.dateTimestamp)
})

const reducer = (state: TInboxState, action: any): TInboxState => {
    switch (action.type) {
        case 'set-modal': return { ...state, modal: action.payload }
        case 'set-selected-tab': return { ...state, selectedTab: action.payload }
        case 'set-active-inbox-label': {
            if (action.payload === state.activeInboxLabel) return state
            return { ...state, activeInboxLabel: action.payload, query: '' }
        }
        case 'set-conversation-details': return { ...state, conversationDetails: action.payload }

        case 'start-fetching-conversations': return { ...state, fetchingConversations: true }
        case 'end-fetching-conversations': return { ...state, fetchingConversations: false }
        case 'start-fetching-more-conversations': return { ...state, fetchingMoreConversations: true }
        case 'end-fetching-more-conversations': return { ...state, fetchingMoreConversations: false }

        case 'set-conversations': {
            const nextConversations: any[] = Array.isArray(action.payload) ? action.payload : []
            // Keep the active sidebar thread in sync with the latest conversation list payload.
            // Without this, refetches (paginate / bulk tag actions) can update the list but leave
            // `selectedConversation.tags` stale, causing tags to "come back" in the sidebar.
            const selected = state.selectedConversation
            const nextByConversationId = new Map<string, any>(
                nextConversations
                    .filter((c: any) => c?.conversationId)
                    .map((c: any) => [String(c.conversationId), c]),
            )
            if (!selected?.conversationId) {
                // Still update selectedConversations array (bulk selection) so bulk tag modal reopens correctly.
                const nextSelectedConversations = (state.selectedConversations || []).map((c: any) => {
                    const fresh = c?.conversationId ? nextByConversationId.get(String(c.conversationId)) : undefined
                    return fresh ? { ...c, ...fresh } : c
                })
                return { ...state, conversations: nextConversations, selectedConversations: nextSelectedConversations }
            }
            const freshSelected = nextConversations.find((c: any) => c?.conversationId === selected.conversationId)
            return {
                ...state,
                conversations: nextConversations,
                selectedConversation: freshSelected ? { ...selected, ...freshSelected } : selected,
                selectedConversations: (state.selectedConversations || []).map((c: any) => {
                    const fresh = c?.conversationId ? nextByConversationId.get(String(c.conversationId)) : undefined
                    return fresh ? { ...c, ...fresh } : c
                }),
            }
        }

        /**
         * After bulk tag apply/remove (Connections modal), update inbox conversations deterministically
         * without relying on a subsequent paginate refetch.
         * Payload is a map: { [connectionId]: tags[] }.
         */
        case 'bulk-update-tags-by-connection': {
            const map = (action.payload && typeof action.payload === 'object') ? action.payload : {}

            const resolveTagPatch = (conv: any): any[] | undefined => {
                const cid = leanObjectIdString(conv?.connectionId)
                if (cid && map[cid] !== undefined) return map[cid]
                if (Array.isArray(conv?.connections)) {
                    for (const ref of conv.connections) {
                        const rid = leanObjectIdString(ref)
                        if (rid && map[rid] !== undefined) return map[rid]
                    }
                }
                return undefined
            }
            const patchList = (list: any[] | undefined) => (list || []).map((conv: any) => {
                const nextTags = resolveTagPatch(conv)
                return nextTags !== undefined ? { ...conv, tags: nextTags } : conv
            })

            const nextConversations = patchList(state.conversations)
            const nextFiltered = patchList(state.filteredConversations)
            const nextPinned = patchList(state.pinnedConversationsArray)

            const patchSelectedConversation = (conv: any) => {
                const nextTags = resolveTagPatch(conv)
                return nextTags !== undefined ? { ...conv, tags: nextTags } : conv
            }

            return {
                ...state,
                conversations: nextConversations,
                filteredConversations: nextFiltered,
                pinnedConversationsArray: nextPinned,
                selectedConversation: state.selectedConversation ? patchSelectedConversation(state.selectedConversation) : state.selectedConversation,
                selectedConversations: (state.selectedConversations || []).map(patchSelectedConversation),
            }
        }
        case 'set-filtered-conversations': return { ...state, filteredConversations: action.payload }
        case 'set-last-fetched-conversation': return { ...state, lastFetchedConversation: action.payload }
        case 'set-pinned-conversations': return { ...state, pinnedConversationsArray: action.payload }
        case 'add-more-conversations': {
            const incoming: any[] = Array.isArray(action.payload) ? action.payload : []
            if (!incoming.length) return state
            const existingIds = new Set(state.conversations.map((c: any) => c.conversationId))
            const deduped = incoming.filter((c: any) => c?.conversationId && !existingIds.has(c.conversationId))
            if (!deduped.length) return state
            return { ...state, conversations: sortConversations([...state.conversations, ...deduped]) }
        }
        case 'add-more-filtered-conversations': return { ...state, filteredConversations: [...state.filteredConversations, ...action.payload] }

        /**
         * Merge fresh page-1 conversations into existing list WITHOUT wiping later pages.
         * Existing conversations are updated, brand-new conversations are added, then the full
         * loaded list is re-sorted to match backend pagination (pinned first, newest first).
         */
        case 'merge-front-page-conversations': {
            const fresh: any[] = Array.isArray(action.payload) ? action.payload : []
            if (!fresh.length) return state
            const freshById = new Map<string, any>(fresh.map((c: any) => [c.conversationId, c]))
            const existingIds = new Set(state.conversations.map((c: any) => c.conversationId))
            const updatedExisting = state.conversations.map((c: any) => {
                const f = freshById.get(c.conversationId)
                return f ? { ...c, ...f } : c
            })
            const newOnes = fresh.filter((c: any) => !existingIds.has(c.conversationId))
            return { ...state, conversations: sortConversations([...newOnes, ...updatedExisting]) }
        }

        case 'set-all-conversations-fetched': return { ...state, allConversationsFetched: true }
        case 'set-all-conversations-loaded': return { ...state, allConversationLoaded: true }
        case 'reset-all-conversations-fetched': return {
            ...state,
            allConversationsFetched: false,
            linkedinNextCursor: undefined,
            salesNavigatorNextCursor: undefined,
        }
        case 'set-linkedin-next-cursor': return { ...state, linkedinNextCursor: action.payload }
        case 'set-sales-navigator-next-cursor': return { ...state, salesNavigatorNextCursor: action.payload }
        case 'set-sales-navigator-data': return { ...state, salesNavigatorData: action.payload }
        case 'set-selected-conversations': return { ...state, selectedConversations: action.payload }

        case 'update-conversations-tags': return {
            ...state,
            conversations: state.conversations.map(conv => {
                const updated = action.payload.find((u: any) => u.conversationId === conv.conversationId)
                return updated?.tags?.length ? { ...conv, tags: updated.tags } : conv
            })
        }
        case 'update-one-of-conversations-tags': return {
            ...state,
            conversations: state.conversations.map(conv =>
                conv.conversationId === action.payload.conversationId ? { ...conv, tags: action.payload.tags } : conv
            )
        }

        case 'set-filtering-tags': return { ...state, filteringTags: action.payload }
        case 'set-number-of-filters': return { ...state, numberOfFilters: action.payload }
        case 'set-pinned-filter': return { ...state, pinned: action.payload }
        case 'set-starred-filter': return { ...state, starred: action.payload }
        case 'set-readed-filter': return { ...state, readed: action.payload }
        case 'set-tagged-filter': return { ...state, tagged: action.payload }
        case 'set-tags-filter': return { ...state, tags: [...action.payload] }
        case 'set-archived-filter': return { ...state, archived: action.payload }
        case 'set-last-messaged-from-filter': return { ...state, lastMessagedFrom: action.payload }
        case 'set-last-messaged-to-filter': return { ...state, lastMessagedTo: action.payload }
        case 'reset-last-messaged-filter': return {
            ...state,
            lastMessagedFrom: '',
            lastMessagedTo: '',
            lastMessagedOnlyLast: false,
            conversationOwners: undefined,
            conversationStatus: { value: 'all', label: 'All conversations' },
        }
        case 'set-conversation-status-filter': return { ...state, conversationStatus: action.payload }
        case 'set-last-messaged-only-last': return { ...state, lastMessagedOnlyLast: !!action.payload }
        case 'set-conversation-owners-filter': return { ...state, conversationOwners: action.payload }
        case 'reset-filters': return {
            ...state,
            pinned: action.payload,
            starred: action.payload,
            readed: action.payload,
            tagged: action.payload,
            archived: 'No',
            tags: [],
            lastMessagedFrom: '',
            lastMessagedTo: '',
            lastMessagedOnlyLast: false,
            conversationOwners: undefined,
            conversationStatus: { value: 'all', label: 'All conversations' },
        }
        case 'set-search-value': return { ...state, query: action.payload }

        case 'set-selected-participants': return { ...state, selectedParticipants: action.payload }
        case 'set-message-attachments': return { ...state, messageAttachments: action.payload }
        case 'set-applied-template': return { ...state, appliedTemplate: action.payload }
        case 'set-connections-search-query-name': return { ...state, searchConnectionsQuery: { ...state.searchConnectionsQuery, searchByName: action.payload } }

        case 'set-displayed-conversation': return { ...state, selectedConversation: action.payload }
        case 'update-conversation-to-read': return {
            ...state,
            conversations: state.conversations.map(el =>
                el.conversationId === action.payload.conversationId
                    ? { ...el, ...action.payload, isRead: true }
                    : el
            )
        }
        case 'update-conversation-to-unread': return { ...state, conversations: state.conversations.map(el => el.conversationId === action.payload.conversationId ? { ...el, isRead: false } : el) }
        case 'update-conversation': {
            const conversations = state.conversations.map(conv =>
                conv.conversationId === action.payload.conversationId ? { ...conv, ...action.payload } : conv
            )
            const selectedConversation =
                state.selectedConversation?.conversationId === action.payload.conversationId
                    ? { ...state.selectedConversation, ...action.payload }
                    : state.selectedConversation
            return { ...state, conversations, selectedConversation }
        }
        case 'update-conversations': return {
            ...state,
            conversations: state.conversations.map(conv =>
                action.payload.conversationIds.includes(conv.conversationId)
                    ? { ...conv, ...action.payload.conversationParameters }
                    : conv
            )
        }
        case 'set-selected-conversation': return { ...state, selectedConversation: action.payload, fetchingMessages: !!action.payload, messages: [], allMessagesFetched: true, selectedParticipants: [] }
        case 'clear-inbox-on-source-tab-change': {
            const tab =
                action.payload === 'SalesNavigator' ? 'SalesNavigator' : action.payload === 'Inbox' ? 'Inbox' : state.selectedTab
            return {
                ...state,
                selectedTab: tab,
                query: '',
                selectedConversations: [],
                selectedConversation: null,
                selectedProfile: null,
                selectedConnection: null,
                selectedParticipants: [],
                pinned: 'All',
                starred: 'All',
                readed: 'All',
                tagged: 'All',
                archived: 'No',
                lastMessagedFrom: '',
                lastMessagedTo: '',
                lastMessagedOnlyLast: false,
                conversationOwners: undefined,
                conversationStatus: { value: 'all', label: 'All conversations' },
                activeInboxLabel: 'mainInbox',
                messages: [],
                allMessagesFetched: true,
                messageAttachments: [],
                fetchingMessages: false,
                fetchingMoreMessages: false,
                /** Same skeleton as filter/tab refetch; list is cleared before `getConversationsFirstPage` runs in useEffect. */
                fetchingConversations: true,
                fetchingMoreConversations: false,
                /** Don't mix Inbox and SalesNav lists until new tab loads its own page. */
                conversations: [],
                filteredConversations: [],
                pinnedConversationsArray: [],
                allConversationsFetched: false,
                linkedinNextCursor: undefined,
            }
        }
        case 'reset-selected-conversation': return { ...state, selectedConversation: null }
        case 'update-selected-conversation': return { ...state, selectedConversation: action.payload }
        case 'update-selected-conversation-read-unread': return { ...state, selectedConversation: { ...state.selectedConversation, ...action.payload } }

        case 'set-selected-profile': return { ...state, selectedProfile: action.payload }
        case 'set-selected-connection': return { ...state, selectedConnection: action.payload }
        case 'reset-selected-connection': return { ...state, selectedConnection: null }

        case 'start-fetching-messages': return { ...state, fetchingMessages: true }
        case 'start-fetching-more-messages': return { ...state, fetchingMoreMessages: true }
        case 'end-fetching-messages': return { ...state, fetchingMessages: false }
        case 'end-fetching-more-messages': return { ...state, fetchingMoreMessages: false }
        case 'set-messages': return { ...state, messages: action.payload, allMessagesFetched: false }
        case 'add-more-messages': return { ...state, messages: [...action.payload, ...state.messages] }
        case 'add-last-batch-of-messages': return { ...state, messages: [...action.payload, ...state.messages], allMessagesFetched: true }
        case 'add-sent-message': return { ...state, messages: [...state.messages, action.payload] }
        case 'set-all-messages-fetched': return { ...state, allMessagesFetched: true }
        case 'update-message': return {
            ...state,
            messages: state.messages.map(msg => msg.tempId === action.payload.tempId ? { ...msg, ...action.payload } : msg)
        }
        case 'delete-message': return {
            ...state,
            messages: state.messages.filter(msg => msg.tempId !== action.payload.tempId)
        }

        case 'set-shared-accounts': return { ...state, sharedAccounts: Array.isArray(action.payload) ? action.payload : [] }
        case 'set-shared-account-filter': return {
            ...state,
            sharedAccountFilter: Array.isArray(action.payload) ? action.payload : [],
            selectedConversations: [],
        }
        case 'set-messages-cursor': return { ...state, messagesCursor: action.payload ?? null }

        default: return state
    }
}

const { StoreProvider: InboxStoreProvider, useStoreContext: useInboxStore } = makeStore(reducer, initialState)

export { InboxStoreProvider, useInboxStore }
